import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Text,
  Input,
  InputGroup,
  InputLeftElement,
  Button,
  Icon,
  HStack,
  VStack,
  Circle,
  Spinner,
  useToast
} from '@chakra-ui/react';
import { FiSearch, FiArrowRight } from 'react-icons/fi';
import { useHistory } from 'react-router-dom';
import { searchCustomers } from 'services/unifiedCustomersSearch';
import AnchoredDropdown from 'components/Search/AnchoredDropdown';

const getInitials = (first = '', last = '') =>
  ((first?.[0] || '') + (last?.[0] || '')).toUpperCase() || '?';

const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

const HomeHero = ({ user }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const history = useHistory();
  const toast = useToast();
  const searchAnchorRef = useRef(null);

  const runSearch = async (term) => {
    if (!term) {
      setSearchResults([]);
      return;
    }
    setIsSearching(true);
    try {
      const results = await searchCustomers(term, 8);
      setSearchResults(results || []);
      setDropdownOpen(true);
    } catch (error) {
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const debouncedSearch = useCallback(debounce((term) => runSearch(term), 350), []);

  useEffect(() => {
    const term = searchTerm.trim();
    if (term) {
      debouncedSearch(term);
    } else {
      setSearchResults([]);
      setDropdownOpen(false);
    }
  }, [searchTerm, debouncedSearch]);

  const handleSelectResult = (customer) => {
    if (!customer?.id) return;
    history.push(`/admin/usuario/${customer.id}`);
    setSearchTerm('');
    setSearchResults([]);
    setDropdownOpen(false);
  };

  // Atajo: Enter/botón "Buscar" abre directamente si hay un único resultado;
  // si hay varios, simplemente despliega el dropdown para elegir.
  const handleSearch = () => {
    const term = searchTerm.trim();
    if (!term) return;

    if (searchResults.length === 1) {
      handleSelectResult(searchResults[0]);
    } else if (searchResults.length > 1) {
      setDropdownOpen(true);
    } else if (!isSearching) {
      toast({
        title: 'Sin resultados',
        description: `No encontramos coincidencias para "${term}"`,
        status: 'info',
        duration: 3000,
        isClosable: true,
        position: 'top-right'
      });
    }
  };

  const todayLabel = (() => {
    const formatted = new Intl.DateTimeFormat('es-CL', {
      weekday: 'long',
      day: 'numeric',
      month: 'long'
    }).format(new Date());
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  })();

  return (
    <Box
      bgGradient="linear(125deg, #F2683C 0%, #E8431A 58%, #D23A14 100%)"
      borderRadius="28px"
      p={{ base: '24px', md: '28px 32px' }}
      color="white"
      position="relative"
      overflow="hidden"
      boxShadow="0 20px 48px -20px rgba(216,64,26,.65)"
    >
      <Box
        position="absolute"
        right="-50px"
        top="-80px"
        w="300px"
        h="300px"
        borderRadius="full"
        bg="rgba(255,255,255,.1)"
        pointerEvents="none"
      />
      <Box
        position="absolute"
        right="140px"
        bottom="-130px"
        w="220px"
        h="220px"
        borderRadius="full"
        bg="rgba(255,255,255,.07)"
        pointerEvents="none"
      />

      <Box position="relative">
        <Text fontSize={{ base: '22px', md: '29px' }} fontWeight="800" letterSpacing="-0.03em" lineHeight="1.1">
          Hola, {user?.first_name || 'Usuario'} 👋
        </Text>
        <Text fontSize="14px" fontWeight="500" opacity={0.93} mt="6px" textTransform="capitalize">
          {todayLabel}
        </Text>

        <Box position="relative" mt="20px" ref={searchAnchorRef}>
          <InputGroup size="lg">
            <InputLeftElement h="60px" pl="6px">
              <Icon as={FiSearch} color="coral.700" boxSize="22px" />
            </InputLeftElement>
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Busca un alumno por nombre, RUT, apoderado o correo…"
              bg="white"
              color="ink.900"
              h="60px"
              borderRadius="16px"
              border="none"
              pl="54px"
              pr="140px"
              fontSize="16px"
              fontWeight="500"
              boxShadow="0 16px 36px -14px rgba(120,30,0,.45)"
              _focus={{ boxShadow: '0 16px 36px -14px rgba(120,30,0,.45)' }}
              _placeholder={{ color: 'ink.400' }}
            />
          </InputGroup>
          <Button
            position="absolute"
            right="10px"
            top="10px"
            h="40px"
            px="20px"
            borderRadius="12px"
            bg="coral.500"
            color="white"
            fontWeight="700"
            fontSize="14px"
            rightIcon={<Icon as={FiArrowRight} />}
            onClick={handleSearch}
            isLoading={isSearching}
            _hover={{ bg: 'coral.600' }}
          >
            Buscar
          </Button>

          <AnchoredDropdown
            anchorRef={searchAnchorRef}
            isOpen={dropdownOpen && searchTerm.trim().length > 0}
            onClose={() => setDropdownOpen(false)}
          >
            {isSearching && searchResults.length === 0 ? (
              <HStack p={4} spacing={3} justify="center">
                <Spinner size="sm" color="coral.500" thickness="2px" />
                <Text fontSize="13px" color="ink.400">Buscando...</Text>
              </HStack>
            ) : searchResults.length > 0 ? (
              searchResults.map((customer) => {
                const fullName = customer.name || customer.display_text ||
                  `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || 'Sin nombre';
                return (
                  <Box
                    key={customer.id}
                    p={3}
                    cursor="pointer"
                    borderBottom="1px solid"
                    borderColor="cream.line"
                    _last={{ borderBottom: 'none' }}
                    _hover={{ bg: 'coral.tint' }}
                    transition="background .13s"
                    onClick={() => handleSelectResult(customer)}
                  >
                    <HStack spacing={3}>
                      <Circle size="32px" bg="coral.500" color="white" fontSize="xs" fontWeight="700" flexShrink={0}>
                        {getInitials(customer.first_name, customer.last_name)}
                      </Circle>
                      <VStack align="start" spacing={0} flex={1} minW={0}>
                        <Text fontSize="13.5px" fontWeight="700" color="ink.900" noOfLines={1}>
                          {fullName}
                        </Text>
                        <Text fontSize="12px" color="ink.400" noOfLines={1}>
                          {customer.email || customer.username}
                        </Text>
                      </VStack>
                      {customer.group && (
                        <Box as="span" px="8px" py="2px" borderRadius="999px" bg="sky.bg" color="sky.ink"
                          fontSize="11px" fontWeight="700" flexShrink={0}>
                          {customer.group}
                        </Box>
                      )}
                    </HStack>
                  </Box>
                );
              })
            ) : (
              <Text fontSize="13px" color="ink.400" textAlign="center" p={4}>
                Sin resultados para "{searchTerm}"
              </Text>
            )}
          </AnchoredDropdown>
        </Box>
      </Box>
    </Box>
  );
};

export default HomeHero;
